import { LegacyForm, LegacyInspection, LegacyItem, LegacyTemplate } from '@/ui/components/inspectionReport/legacyTypes';
import {
  Action,
  ConditionalSection,
  Form,
  HeaderPage,
  Page,
  Section,
  UnknownItem,
} from '@/ui/components/inspectionReport/items';
import { h, VNode } from 'vue';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import { ResponsesTypes } from '@/entities/checklist';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';
import { ImageAsset } from '@/ui/components/asset/ImageAsset';
import { makeQuestion } from '@/ui/components/inspectionReport/items/Question';
import { InspectionItemType } from '@/entities/inspection/constants';
import { DocumentAsset } from '@/ui/components/asset/DocumentAsset';
import { t } from '@/ui/i18n/i18n';
import { isPdf } from '@/ui/helpers/systemHelper';
import QuestionTitle from '@/ui/components/inspectionReport/items/QuestionTitle.vue';

export default class ItemFactory {
  resource: LegacyInspection | null;
  template: LegacyTemplate | null | undefined;
  isTranslationEnabled = false;

  constructor() {
    const { resource, isTranslationEnabled } = useInspectionReport();
    this.resource = resource.value;
    this.template = this.resource?.template;
    this.isTranslationEnabled = isTranslationEnabled.value;
  }

  getItem(item: LegacyItem | LegacyForm, isLowQualityImages = false) {
    if (!item) {
      return null;
    }

    const itemComponent = {
      [InspectionItemType.Form]: Form,
      [InspectionItemType.Page]:
        item.params?.header
          ? HeaderPage
          : Page,
      [InspectionItemType.Section]: Section,
      [InspectionItemType.Conditional]: ConditionalSection,
    };

    switch (item.type) {
      case InspectionItemType.Action:
        return this.getAction(item as LegacyItem);
      case InspectionItemType.Question:
        return this.getQuestion(item as LegacyItem, isLowQualityImages);
      case InspectionItemType.FormAction:
        return null;
      default:
        return h(itemComponent[item.type] || UnknownItem, {
          props: {
            item,
          },
        });
    }
  }

  private getQuestion(question: LegacyItem, isLowQualityImages = false) {
    if (!this.shouldDisplayQuestion(question)) {
      return null;
    }

    const { resourceIssues } = useInspectionReport();

    if ([ResponsesTypes.instruction, ResponsesTypes.entityItem].includes(question.response_type as ResponsesTypes)) {
      return makeQuestion(question);
    }

    if (!this.isOptionEnabled(question.response_type)) {
      return h('table', { class: 'inspection-report__table' }, [
        h('tr', [
          h(TableBox, [
            question.image_id || question.image
              ? h(ImageAsset, {
                class: 'mb-2 inspection-report__question-img',
                props: {
                  photo: question,
                  isLowQualityImages: isPdf && isLowQualityImages,
                },
              })
              : null,
            h('span', {
              class: 'font-semibold',
            }, [
              this.getQuestionTile(question),
              h(DocumentAsset, { props: { item: question } }),
            ]),
          ]),
          h(TableBox, [makeQuestion(question)]),
        ]),
        question.notes ? h('tr', [
          h(TableBox, {
            props: {
              colspan: 2,
            },
          }, [
            h('div', { class: 'pl-2' }), [
              h('span', [`${t('inspection-report.note')}: `]),
              h('span', { class: 'whitespace-pre-line' }, [question.notes]),
            ],
          ]),
        ]) : null,
        question.photos && question.photos.length > 0
          ? h('tr', [
            h(TableBox, {
              props: {
                colspan: 2,
              },
            }, [
              h('div', { class: 'flex pl-2 report-list-question-photo' }, [
                question.photos.map(photo => h('div', { class: 'flex items-top' }, [
                  h('span', { class: 'mr-1 pt-1' }, [`${photo.index?.toString()}.`]),
                  h(ImageAsset, {
                    class: 'mr-4 inline-block',
                    props: {
                      photo,
                      isThumbnail: true,
                      isLowQualityImages: isPdf && isLowQualityImages,
                    },
                  }),
                ])),
              ]),
            ]),
          ])
          : null,
      ]);
    }

    const inFormIssue = this.template?.display_issues
      && resourceIssues.value.find(issue => issue.payload && issue.payload.item_uuid === question.uuid);

    return h('div', [
      h('table', { class: 'inspection-report__table' }, [makeQuestion(question)]),
      inFormIssue
      && h('table', {
        class: 'inspection-report__table action-table',
        attrs: { 'data-test': 'action-table' },
      }, [
        h(Action, {
          props: {
            item: inFormIssue,
            displayQuery: true,
            displayTitles: true,
          },
        }),
      ]),
    ]);
  }

  private isQuestionInHeader(uuid: string): boolean {
    return useInspectionReport().resourceHeaderItems.value.includes(uuid);
  }

  private getQuestionTile(question: LegacyItem): string|VNode {
    switch (true) {
      case question.response_type === ResponsesTypes.dateTimeAutomatic:
        return this.isTranslationEnabled && question.title_translated
          ? question.title_translated
          : t('inspection-report.completed-at');
      case question.response_type === ResponsesTypes.userAutomatic:
        return this.isTranslationEnabled && question.title_translated
          ? question.title_translated
          : t('inspection-report.prepared-by');
      case question.response_type === ResponsesTypes.entityItemAutomatic:
      case question.response_type === ResponsesTypes.entityItem && this.isQuestionInHeader(question.uuid):
        return t('common.entity-item');
      default:
        return h(QuestionTitle, { props: { question } });
    }
  }

  private getAction(action: LegacyItem) {
    const { resourceIssues } = useInspectionReport();

    if (!this.template?.display_issues) {
      return null;
    }

    const item = resourceIssues.value.find(issue => issue.payload?.item_uuid === action.uuid);

    if (!item) {
      return null;
    }

    return h('table', {
      class: 'inspection-report__table action-table',
      attrs: { 'data-test': 'action-table' },
    }, [
      h(Action, {
        props: {
          item,
          displayQuery: true,
          displayTitles: true,
        },
      }),
    ]);
  }

  private shouldDisplayQuestion(question: LegacyItem) {
    if (question.response_type === ResponsesTypes.instruction) {
      if (this.template?.exclude_questions_type_instruction) {
        return false;
      }

      return true;
    }

    // exclude automatic items
    if (!question.params || (question.params && question.params.automatic !== true)) {
      if (this.template?.exclude_questions_not_responded && !question.responded) {
        return false;
      }
    }

    return true;
  }

  private isOptionEnabled(responseType: string): boolean {
    return [ResponsesTypes.list, ResponsesTypes.file].includes(responseType as ResponsesTypes);
  }
}
